import styles from "./variableValueModal.module.scss";
import { ModalWindow } from "../../../shared/components/modalWindow/modalWindow";
import { useTranslation } from "react-i18next";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  object,
  string
} from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input } from "../../../shared/components/input/input";
import {
  Variable,
  VariableValue
} from "../../../types/variableType";
import {
  useCreateVariableValueMutation,
  useUpdateVariableValueMutation
} from "../../../api/variableService";
import { MessageWindow } from "../../../shared/components/messageWindow/messageWindow";

type VariableValueModalProps = {
  onClose: (isSave: boolean, newVariableId?: number) => void;
  editVariable: Variable;
  editVariableValue?: VariableValue;
}

export const VariableValueModal = ({ onClose, editVariable, editVariableValue }: VariableValueModalProps) => {
  const { t } = useTranslation();

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const [createVariableValue, { isSuccess: isSuccessCreateVariableValue, isLoading: isLoadingCreate }] = useCreateVariableValueMutation();
  const [updateVariableValue, { isSuccess: isSuccessUpdateVariableValue, isLoading: isLoadingUpdate }] = useUpdateVariableValueMutation();

  useEffect(() => {
    if (isSuccessCreateVariableValue) {
      onClose(true);
    }
  }, [isSuccessCreateVariableValue, onClose]);

  useEffect(() => {
    if (isSuccessUpdateVariableValue) {
      onClose(true);
    }
  }, [isSuccessUpdateVariableValue, onClose]);

  const schema = object({
    value: string().max(50000, t("common.notValid"))
      .test("unique", t("common.notUnique"), (value) =>
        !editVariable.values.filter(item => item.id !== editVariableValue?.id).some(item => item.value === value)),
  });

  const {
    control,
    handleSubmit, formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: { value: editVariableValue?.value || "" }
  });

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose(false);
    }
  }, [isDirty, onClose]);

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose(false);
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);

  const onClickSave = (): void => {
    handleSubmit((result) => {
      if (!!editVariableValue) {
        updateVariableValue({ id: editVariableValue.id, value: result.value || "" });
      } else {
        createVariableValue({ variableId: editVariable.id, value: result.value || "" });
      }
    })();
  }

  const title = useMemo(() => {
    if (!!editVariableValue) {
      return t("variableManager.editValue")
        .replace("{{variableValue}}", `"${editVariableValue.value}"`)
        .replace("{{variableName}}", `"${editVariable.name}"`)
    } else {
      return t("variableManager.addValue")
        .replace("{{variableName}}", `"${editVariable.name}"`)
    }
  }, [t, editVariableValue, editVariable])

  return (
    <>
      <ModalWindow
        title={title}
        onClose={onCancel}
        width={380}
        children={
          <div className={styles.container}>
            <div className={styles.inputContainer}>
              <div
                className={styles.label}>{!!editVariableValue?.id ? t("testCaseTemplate.value") : t("variableManager.enterValue")}</div>
              <div className={styles.input}>
                <Input control={control}
                       name={"value"}
                       error={!!errors.value ? errors.value.message : ""}/>
              </div>
            </div>
            <div className={styles.buttonsContainer}>
              <button className={styles.cancel}
                      disabled={isLoadingCreate || isLoadingUpdate}
                      onClick={onCancel}>{t("common.cancel")}</button>
              <button className={styles.save}
                      disabled={isLoadingCreate || isLoadingUpdate}
                      onClick={onClickSave}>{t("common.save")}</button>
            </div>
          </div>
        }
      />

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>
  );
}